import React, { useCallback, useEffect, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';

/**
 * 店舗の追加申請の承認キュー（A-6）。
 *
 * 申請は Google Places の候補から選んで出されるので、**店名・住所・座標・place_id は
 * すでに揃っている**。ここで人が決めるのは2つだけ:
 *
 * 1. **その店は本当にまだ無いか。** 同じ店がモールの住所と自分の入口の2回入って
 *    いることがあり、place_id が違うので機械には重複と分からない。だから
 *    半径200mの既存店舗を**同じ画面に**出す。別画面に置くと誰も見ない
 * 2. **クレーンゲームがある店か。** 申請の「補足」がその唯一の材料
 *
 * 店名と都道府県だけ直せる。Places は「イオン◯◯店 2F ゲームコーナー」のような
 * 名前を返し、住所が区から始まる店では都道府県が読めない。座標と place_id は
 * 触らせない —— それを運営が打ち直せるなら、候補から選ばせた意味が無くなる。
 */
export default function StoreAddRequests() {
  useAuthGuard();
  const [items, setItems] = useState(null);
  const [pending, setPending] = useState(0);
  const [status, setStatus] = useState('pending');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/store-add-requests?status=${status}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setItems((body.data && body.data.items) || []);
      setPending((body.data && body.data.pending) || 0);
    } catch (e) {
      setError('申請を読み込めませんでした: ' + e.message);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (item, name, prefecture) => {
    if (!prefecture) {
      return setError('都道府県を指定してください（住所から読めませんでした）');
    }
    setBusy(item.id);
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/store-add-requests/${item.id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ name, prefecture }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body.error && body.error.message) || `HTTP ${res.status}`);
      }
      await load();
    } catch (e) {
      setError('承認できませんでした: ' + e.message);
    } finally {
      setBusy('');
    }
  };

  const reject = async (item) => {
    // 理由は必須。同じ店がまた申請されるので、次に読む人の判断材料になる。
    const reason = window.prompt('却下の理由（次に同じ店が申請されたときに読む値になります）');
    if (!reason || !reason.trim()) return;
    setBusy(item.id);
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/store-add-requests/${item.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) {
      setError('却下できませんでした: ' + e.message);
    } finally {
      setBusy('');
    }
  };

  return (
    <Layout>
      <div className="container-fluid py-3">
        <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
          <h4 className="mb-0">店舗の追加申請</h4>
        <span className="badge bg-primary-subtle text-primary-emphasis">未処理 {pending}件</span>
        <select
          className="form-select form-select-sm"
          style={{ width: 'auto' }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="pending">未処理</option>
          <option value="approved">承認済み</option>
          <option value="rejected">却下</option>
          <option value="all">すべて</option>
        </select>
        <button className="btn btn-phoenix-secondary btn-sm" type="button" onClick={load}>
          再読み込み
        </button>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {items === null && <div className="text-muted">読み込み中...</div>}
      {items !== null && items.length === 0 && (
        <div className="text-muted">
          {status === 'pending' ? '未処理の申請はありません' : '該当する申請はありません'}
        </div>
      )}

        {(items || []).map((item) => (
          <RequestCard
            key={item.id}
            item={item}
            busy={busy === item.id}
            onApprove={approve}
            onReject={reject}
          />
        ))}
      </div>
    </Layout>
  );
}

const STATUS_LABEL = {
  pending: { label: '未処理', className: 'bg-primary-subtle text-primary-emphasis' },
  approved: { label: '承認済み', className: 'bg-success-subtle text-success-emphasis' },
  rejected: { label: '却下', className: 'bg-secondary-subtle text-secondary-emphasis' },
};

function RequestCard({ item, busy, onApprove, onReject }) {
  const [name, setName] = useState(item.name);
  const [prefecture, setPrefecture] = useState(item.prefecture || '');
  const badge = STATUS_LABEL[item.status] || STATUS_LABEL.pending;
  const nearby = item.nearby || [];

  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <span className={`badge ${badge.className}`}>{badge.label}</span>
          <strong>{item.name}</strong>
          <span className="text-muted" style={{ fontSize: '0.8125rem' }}>{item.address}</span>
          <a
            className="ms-auto"
            href={`https://www.google.com/maps/search/?api=1&query=${item.lat},${item.lng}&query_place_id=${item.placeId}`}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: '0.8125rem' }}
          >
            Googleで見る
          </a>
        </div>

        {/* 申請者が書いた補足。**クレーンゲームがあるかどうかの唯一の材料**で、
            ここが空の申請は Google を見に行かないと決められない。 */}
        {item.note ? (
          <div className="mb-2 p-2 rounded" style={{ background: '#f5f7fa', fontSize: '0.875rem' }}>
            {item.note}
          </div>
        ) : (
          <div className="mb-2 text-muted" style={{ fontSize: '0.8125rem' }}>
            補足なし（クレーンゲームの有無は Google の写真で確認してください）
          </div>
        )}

        <div className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>
          申請: {item.submittedByName || '（不明）'} / {new Date(item.createdAt).toLocaleString('ja-JP')}
          {item.createdStoreId && (
            <>
              {' / '}
              <a href={`/stores/${item.createdStoreId}`}>作成された店舗</a>
            </>
          )}
          {item.rejectReason && <> / 却下理由: {item.rejectReason}</>}
        </div>

        {/* 重複の判断材料。非表示の店も出す —— 一番多い重複は
            「クレーンゲームなし」で誰かが非表示にした店で、その複製を承認すると
            その判断が黙って元に戻る。 */}
        {item.status === 'pending' && (
          <div className="mb-3">
            <div className="fw-semibold mb-1" style={{ fontSize: '0.8125rem' }}>
              半径200mの既存店舗 {nearby.length}件
            </div>
            {nearby.length === 0 && (
              <div className="text-muted" style={{ fontSize: '0.8125rem' }}>
                近くに登録済みの店舗はありません
              </div>
            )}
            {nearby.map((n) => (
              <div key={n.id} className="d-flex gap-2 align-items-center" style={{ fontSize: '0.8125rem' }}>
                <span className="badge bg-light text-secondary">{n.distanceM}m</span>
                <a href={`/stores/${n.id}`}>{n.name}</a>
                <span className="text-muted">{n.address}</span>
                {n.hiddenAt && <span className="badge bg-dark">非表示中</span>}
                {!n.placeId && <span className="badge bg-warning-subtle text-warning-emphasis">place_idなし</span>}
              </div>
            ))}
          </div>
        )}

        {item.status === 'pending' && (
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <input
              className="form-control form-control-sm"
              style={{ maxWidth: 280 }}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="店名"
            />
            <input
              className="form-control form-control-sm"
              style={{ maxWidth: 140 }}
              value={prefecture}
              onChange={(e) => setPrefecture(e.target.value)}
              placeholder="都道府県"
            />
            {!item.prefecture && (
              <span className="text-warning-emphasis" style={{ fontSize: '0.75rem' }}>
                住所から都道府県が読めません
              </span>
            )}
            <button
              className="btn btn-success btn-sm"
              type="button"
              disabled={busy}
              onClick={() => onApprove(item, name.trim(), prefecture.trim())}
            >
              承認して店舗を作る
            </button>
            <button
              className="btn btn-phoenix-secondary btn-sm"
              type="button"
              disabled={busy}
              onClick={() => onReject(item)}
            >
              却下
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
